import React, { useState, useEffect } from 'react';
import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [newJob, setNewJob] = useState({
    name: '',
    type: 'categorization', // 'categorization' or 'grading'
    dateRange: {
      start: '',
      end: ''
    },
    userFilter: '',
    batchSize: 50
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const response = await fetch('/api/v1/zoomfabric/jobs');
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
    }
  };

  const createJob = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/zoomfabric/jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newJob)
      });

      if (response.ok) {
        setShowNewJobModal(false);
        setNewJob({
          name: '',
          type: 'categorization',
          dateRange: { start: '', end: '' },
          userFilter: '',
          batchSize: 50
        });
        loadJobs();
      }
    } catch (err) {
      console.error('Failed to create job:', err);
    } finally {
      setLoading(false);
    }
  };

  const controlJob = async (jobId, action) => {
    try {
      const response = await fetch(`/api/v1/zoomfabric/jobs/${jobId}/${action}`, {
        method: 'POST'
      });

      if (response.ok) {
        loadJobs();
      }
    } catch (err) {
      console.error(`Failed to ${action} job:`, err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Processing Jobs</h1>
            <p className="text-lg text-gray-600 mt-2">
              Configure and run analysis jobs on your Zoom call data
            </p>
          </div>
          <button
            onClick={() => setShowNewJobModal(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 flex items-center"
          >
            <PlayIcon className="w-5 h-5 mr-2" />
            New Job
          </button>
        </div>
      </div>

      {/* Jobs List */}
      <div className="space-y-4">
        {jobs.map((job) => (
          <div key={job.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  {job.type === 'categorization' ? (
                    <ChartBarIcon className="w-6 h-6 text-purple-500 mr-3" />
                  ) : (
                    <AcademicCapIcon className="w-6 h-6 text-orange-500 mr-3" />
                  )}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{job.name}</h3>
                    <p className="text-sm text-gray-600 capitalize">{job.type} Job</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                    {job.status}
                  </span>
                  {job.status === 'running' && (
                    <button
                      onClick={() => controlJob(job.id, 'pause')}
                      className="p-2 text-gray-500 hover:text-gray-700"
                    >
                      <PauseIcon className="w-4 h-4" />
                    </button>
                  )}
                  {job.status === 'paused' && (
                    <button
                      onClick={() => controlJob(job.id, 'resume')}
                      className="p-2 text-green-500 hover:text-green-700"
                    >
                      <PlayIcon className="w-4 h-4" />
                    </button>
                  )}
                  {(job.status === 'running' || job.status === 'paused') && (
                    <button
                      onClick={() => controlJob(job.id, 'stop')}
                      className="p-2 text-red-500 hover:text-red-700"
                    >
                      <StopIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-sm">
                  <span className="text-gray-500">Date Range:</span>
                  <p className="font-medium">
                    {job.dateRange ? `${job.dateRange.start} - ${job.dateRange.end}` : 'All time'}
                  </p>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">Progress:</span>
                  <p className="font-medium">{job.processed || 0} / {job.total || 0}</p>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">Success Rate:</span>
                  <p className="font-medium">{job.successRate || 0}%</p>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">Runtime:</span>
                  <p className="font-medium">{job.runtime || '0m'}</p>
                </div>
              </div>

              {job.status === 'running' && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((job.processed || 0) / (job.total || 1)) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        ))}

        {jobs.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs yet</h3>
            <p className="text-gray-500">Create your first processing job to analyze Zoom calls</p>
          </div>
        )}
      </div>

      {/* New Job Modal */}
      {showNewJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Create New Processing Job</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Name</label>
                  <input
                    type="text"
                    value={newJob.name}
                    onChange={(e) => setNewJob({...newJob, name: e.target.value})}
                    placeholder="e.g., Sales Call Analysis - Q4 2024"
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
                  <select
                    value={newJob.type}
                    onChange={(e) => setNewJob({...newJob, type: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  >
                    <option value="categorization">Call Categorization</option>
                    <option value="grading">Call Grading</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={newJob.dateRange.start}
                      onChange={(e) => setNewJob({
                        ...newJob,
                        dateRange: {...newJob.dateRange, start: e.target.value}
                      })}
                      className="w-full p-3 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={newJob.dateRange.end}
                      onChange={(e) => setNewJob({
                        ...newJob,
                        dateRange: {...newJob.dateRange, end: e.target.value}
                      })}
                      className="w-full p-3 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">User Filter (optional)</label>
                  <input
                    type="text"
                    value={newJob.userFilter}
                    onChange={(e) => setNewJob({...newJob, userFilter: e.target.value})}
                    placeholder="Filter by specific users, e.g., sales@company.com"
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Batch Size</label>
                  <input
                    type="number"
                    min="10"
                    max="500"
                    value={newJob.batchSize}
                    onChange={(e) => setNewJob({...newJob, batchSize: parseInt(e.target.value)})}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Number of calls to process per batch</p>
                </div>
              </div>

              <div className="mt-8 flex justify-end space-x-3">
                <button
                  onClick={() => setShowNewJobModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={createJob}
                  disabled={loading || !newJob.name}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  <PlayIcon className="w-4 h-4 mr-2" />
                  {loading ? 'Creating...' : 'Create & Start Job'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}